import STableLayout, {
  DEFAULT_PAGE_SIZE,
  TableLayoutRef,
} from "@/styles/STableLayout";
import { CommonListParams } from "@/types/common";
import { Customer, Price, PriceAdd } from "@/types/sales";
import { App, Button, Flex, Input, Modal, Progress, Spin, Tooltip, Upload, UploadProps } from "antd";
import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useDataList from "@/hooks/useDataList";
import apis from "@/apis/customer";
import priceApis from "@/apis/price";
import useDataAdd from "@/hooks/useDataAdd";
import useDataUpdate from "@/hooks/useDataUpdate";
import useDataDel from "@/hooks/useDataDel";
import { AnyObject } from "antd/es/_util/type";
import useFrontDelTip from "@/hooks/useFrontDelTip";
import PriceForm from "../../../components/SPriceForm";
import usePriceColumn from "../hooks/usePriceColumn";
import { ColumnsType } from "antd/lib/table";
import { generateSupplierPriceTemplate, parseCustomerPriceFile } from "./priceImportUtils";
import ExportExcelButton, { ExportColumn } from "@/components/ExportExcelButton";
import ExportProgressModal from "@/components/ExportProgressModal";
import dayjs from "dayjs";
import { dateFormat, priceFormat } from "@/utils/format";
import ListFilterBar from "@/components/ListFilterBar";
import useCommittedFilters from "@/components/ListFilterBar/hooks/useCommittedFilters";

interface SCustomerPriceProps {
  customer: Customer;
}

interface CustomerPriceFilters {
  keyword: string;
}

const INITIAL_CUSTOMER_PRICE_FILTERS: CustomerPriceFilters = {
  keyword: "",
};

const SCustomerPrice: FC<SCustomerPriceProps> = ({ customer }) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const tableRef = useRef<TableLayoutRef | null>(null);

  const [uploading, setUploading] = useState<boolean>(false);
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [exportProgress, setExportProgress] = useState<number>(0);


  const [userList, total, listLoading, { fetchList, addItem, addItems, rmItem, updateItem }] = useDataList<Price, [string, CommonListParams?]>({ fetchApi: apis.prices });
  const { loading: addLoading, add } = useDataAdd<[PriceAdd], Price>({ api: priceApis.add });
  const { update: updatePrice, loading: updateLoading } = useDataUpdate<[PriceAdd, string]>({ api: priceApis.update });
  const { del } = useDataDel({ api: priceApis.del });

  const {
    draftFilters,
    appliedFilters,
    updateDraftFilters,
    commitFilters,
    replaceFilters,
  } = useCommittedFilters<CustomerPriceFilters>(INITIAL_CUSTOMER_PRICE_FILTERS);
  const { keyword } = draftFilters;
  const [addOpen, setAddOpen] = useState<boolean>(false);
  const [detail, setDetail] = useState<Price | null>(null);

  const { onConfirm } = useFrontDelTip({ cb: (id: string) => onDel(id) });
  const columns = usePriceColumn({ onDel: onConfirm, onDetail: setDetail });

  const [processed, setProcessed] = useState<number>(0);
  const [allNum, setAllNum] = useState<number>(0);

  // 导出列配置
  const exportColumns = (t: any): ExportColumn[] => [
    { key: "product.code", label: t("product.code") },
    { key: "product.name", label: t("product.name") },
    { key: "product.brand1", label: t("product.brand1") },
    { key: "product.brand2", label: t("product.brand2") },
    { key: "sale.taxPrice", label: t("sale.taxPrice") },
    { key: "sale.estimateDays", label: t("sale.estimateDays") },
    { key: "sale.expiredAt", label: t("sale.expiredAt") },
    { key: "sale.customerModel", label: t("sale.customerModel") },
    { key: "sale.mfgr", label: t("sale.mfgr") },
    { key: "common.desc", label: t("common.desc") },
    { key: "product.uom", label: t("product.uom") },
  ];

  // 默认选中的列
  const defaultSelectedColumns = (t: any): string[] => [
    'product.code',
    'product.name',
    'product.brand1',
    'sale.taxPrice',
  ];

  // 导出数据映射函数
  const exportDataMapper = (item: Price, t: any) => {
    return {
      [t('product.code')]: item?.product?.code || '',
      [t('product.name')]: item?.product?.name || '',
      [t('product.brand1')]: item?.product?.model?.toLocaleUpperCase() || '',
      [t('product.brand2')]: item?.product?.t1?.toLocaleUpperCase() || '',
      [t('sale.taxPrice')]: priceFormat(item?.price || ''),
      [t('sale.estimateDays')]: item?.estimateDays?.toString() || '',
      [t('sale.expiredAt')]: item?.expiredAt ? dateFormat(dayjs(item.expiredAt)) : '',
      [t('sale.customerModel')]: item?.customerModel || '',
      [t('sale.mfgr')]: item?.mfgr || '',
      [t('common.desc')]: item?.desc || '',
      [t('product.uom')]: item?.product?.uom?.toLocaleUpperCase() || '',
    };
  };

  // 导出全部数据
  const handleExportAll = async (): Promise<Price[]> => {
    setExportLoading(true);
    setExportProgress(0);

    try {
      const res = await apis.prices(customer.id, {
        body: appliedFilters.keyword,
      });
      const prices = res?.body ?? [];
      setExportProgress(100);
      return prices;
    } catch (error) {
      notification.error({ placement: 'top', message: t('common.exportErr') });
      throw error;
    } finally {
      setExportLoading(false);
      setExportProgress(0);
    }
  };

  useEffect(() => {
    const nextFilters = replaceFilters(INITIAL_CUSTOMER_PRICE_FILTERS);
    fetch(0, nextFilters);
    tableRef.current?.resetTablePage();
    // 切换客户时重置筛选，查询由事件显式提交。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer.id, replaceFilters]);

  const handleImportSupplierPrice: UploadProps['beforeUpload'] = async (file) => {
    setUploading(true);
    setProcessed(0);
    try {
      const addList = await parseCustomerPriceFile(file, t, customer.id);
      setAllNum(addList.length)

      // 批量导入
      const successItems: Price[] = [];
      const failedItems: string[] = [];

      for (const item of addList) {
        try {
          const res = await add(item);
          successItems.push(res);
        } catch (err: any) {          
          failedItems.push(item.productId);
        }
        setProcessed(prev => prev + 1)
      }

      // 批量更新列表数据
      if (successItems.length > 0) {
        addItems(successItems);
      }

      // 显示导入结果
      if (failedItems.length === 0) {
        notification.success({
          placement: 'top',
          message: t("purchase.importSuccess"),
          description: t("purchase.importSuccessDesc", { count: successItems.length }),
        });
      } else {
        notification.warning({
          placement: 'top',
          message: t("purchase.importCompleted"),
          description: t("purchase.importPartialDesc", {
            success: successItems.length,
            failed: failedItems.length,
            failedList: failedItems.join(', ')
          }),
        });
      }
    } catch (error) {
      notification.error({ placement: 'top', message: t("purchase.fileParseError") });
    } finally {
      setUploading(false);
    }
    return false; // 阻止自动上传
  };

  const handleSearch = () => {
    const nextFilters = commitFilters();
    tableRef.current?.resetTablePage();
    fetch(0, nextFilters);
  };

  const handleClearFilters = () => {
    const nextFilters = replaceFilters(INITIAL_CUSTOMER_PRICE_FILTERS);
    tableRef.current?.resetTablePage();
    fetch(0, nextFilters);
  };

  const headerChildren = (
    <Flex style={{ width: "100%" }} justify="space-between">
      <Flex gap={8}>
        <Button type="primary" onClick={() => setAddOpen(true)}>{t("sale.addPrice")}</Button>
        <Upload
          accept=".xlsx,.xls"
          beforeUpload={handleImportSupplierPrice}
          showUploadList={false}
          disabled={uploading}
        >
          <Button loading={uploading}>{t("sale.importCustomerPrice")}</Button>
        </Upload>
        <Button onClick={() => generateSupplierPriceTemplate(t)}>{t("purchase.downloadPriceTemplate")}</Button>
        <ExportExcelButton
          list={userList}
          columns={exportColumns}
          defaultSelectedColumns={defaultSelectedColumns}
          dataMapper={exportDataMapper}
          fileName="customer_price.xlsx"
          sheetName={t('sale.customerPrice')}
        />
        <ExportExcelButton
          list={userList}
          isAll
          loadData={handleExportAll}
          columns={exportColumns}
          defaultSelectedColumns={defaultSelectedColumns}
          dataMapper={exportDataMapper}
          fileName="customer_price_all.xlsx"
          sheetName={t('sale.customerPrice')}
        />
      </Flex>
      <div style={{ flex: 1, minWidth: 0 }}>
        <ListFilterBar
          onSearch={handleSearch}
          onReset={handleClearFilters}
          disabled={listLoading}
        >
          {({ submit }) => (
            <Tooltip title={t("sale.bySearchPrice")}>
              <Input
                value={keyword}
                onChange={event =>
                  updateDraftFilters({ keyword: event.target.value.trim() })
                }
                placeholder={t("sale.bySearchPrice")}
                onPressEnter={submit}
                style={{ width: 200 }}
              />
            </Tooltip>
          )}
        </ListFilterBar>
      </div>
    </Flex>
  );

  const fetch = (offset: number, filters: CustomerPriceFilters) => {
    const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE
    fetchList(customer.id, {
      body: filters.keyword,
      pagination: { offset, take },
    });
  };

  const onDel: (id: string) => Promise<void> = (id) => {
    return del(id).then(() => {
      rmItem(id);
      notification.success({ placement: 'top', message: t("common.rmSuccess") });
    });
  };

  return (
    <Flex vertical style={{ width: '100%', height: '100%' }}>
      <STableLayout
        loading={listLoading}
        dataSource={userList}
        columns={columns as ColumnsType<AnyObject>}
        scrollX={1600}
        headerChildren={headerChildren}
        tableLayoutRef={tableRef}
        total={total}
        handleChange={offset => fetch(offset, appliedFilters)}
      />
      <PriceForm
        key={'addPrice'}
        open={addOpen}
        confirmLoading={addLoading}
        handleCancel={() => setAddOpen(false)}
        customer={customer}
        handleOk={(data) => {
          return new Promise((resolve, reject) => {
            add({ ...data, customerId: customer.id })
              .then((res) => {
                addItem(res);
                setAddOpen(false);
                notification.success({ placement: 'top', message: t('common.addSuccess') })
                resolve();
              })
              .catch((err) => {
                const map: {
                  [key in number]: string;
                } = {
                  10010: t('common.networkError'),
                  71601: t('sale.exitsPrice'),
                  71002: t('sale.customerNotExist')
                };
                notification.error({
                  placement: 'top',
                  message: t('common.addError'),
                  description: map[err] ?? t('common.serverError'),
                });
              });
          });
        }}
        leftTitle={t('common.add')}
        data={null}
      />
      <PriceForm
        key={detail?.id}
        open={!!detail}
        customer={customer}
        confirmLoading={updateLoading}
        handleCancel={() => setDetail(null)}
        handleOk={(data) => {
          return new Promise((resolve, reject) => {
            if (detail) {
              updatePrice({ ...data, customerId: customer.id }, detail.id)
                .then(() => {
                  updateItem({ ...detail, ...data });
                  setDetail(null);
                  notification.success({ placement: 'top', message: t('sale.updateSuccess') })
                  resolve();
                })
                .catch((err) => {
                  const map: {
                    [key in number]: string;
                  } = {
                    10010: t('common.networkError'),
                  };
                  notification.error({
                    placement: 'top',
                    message: t('common.addError'),
                    description: map[err.code] ?? t('common.serverError'),
                  });
                  reject();
                });
            }
          });
        }}
        leftTitle={t('common.edit')}
        data={detail}
      />
      {
        uploading && <Modal title={t('sale.importCustomerPrice') + "..."} closable={false} footer={null} maskClosable={false} open={uploading} centered width={1000} >
          <Flex style={{ marginTop: 120, marginBottom: 100 }} justify="center" >
            <Spin spinning={uploading} size="large" />
          </Flex>
          <Progress percent={Number((processed / allNum * 100).toFixed(2))} />
          <span>{`${t('sale.importCustomerPrice')}：${processed}/${allNum}`}</span>
        </Modal>
      }
      <ExportProgressModal visible={exportLoading} progress={exportProgress} />
    </Flex>
  );
};

export default SCustomerPrice;
